class Favorites {
    constructor() {
        this.favorites = this.getFavoritesFromStorage();
        this.init();
        this.bindEvents();
        this.updateFavoriteCounter();
        this.updateFavoriteButtons();
    }

    init() {
        // Create favorites modal if it doesn't exist
        if (!document.getElementById("favorites-modal")) {
            this.createFavoritesModal();
        }
    }

    bindEvents() {
        // Handle favorite button clicks - both product card and product page buttons
        document.addEventListener(
            "click",
            (e) => {
                const favoriteButton =
                    e.target.closest(".favorite-button") ||
                    e.target.closest(
                        ".product-page-favorite-button"
                    );
                if (favoriteButton) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleFavoriteToggle(
                        favoriteButton
                    );
                    return false; // Prevent any further event bubbling
                }
            },
            true
        ); // Use capture phase to ensure we catch the event first

        // Handle navbar favorites button click (both mobile and desktop)
        document.addEventListener("click", (e) => {
            if (
                e.target.closest(".navbar-favorites-button")
            ) {
                e.preventDefault();
                this.showFavoritesModal();
            }
        });

        // Handle modal events
        document.addEventListener("click", (e) => {
            // Handle go to product page
            if (e.target.closest(".favorite-item-image")) {
                e.preventDefault();
                const productUrl = e.target.closest(
                    ".favorite-item-image"
                ).dataset.productUrl;
                window.location.href = productUrl;
            }

            if (
                e.target.closest(".favorite-item-details")
            ) {
                e.preventDefault();
                const productUrl = e.target.closest(
                    ".favorite-item-details"
                ).dataset.productUrl;
                window.location.href = productUrl;
            }

            // Close modal - use closest() to handle clicks on child elements (like SVG icons)
            if (
                e.target.matches(".favorites-modal-overlay")
            ) {
                e.preventDefault();
                e.stopPropagation();
                this.hideFavoritesModal();
                return;
            }

            if (
                e.target.closest(".favorites-modal-close")
            ) {
                e.preventDefault();
                e.stopPropagation();
                this.hideFavoritesModal();
                return;
            }

            // Clear all favorites
            if (e.target.closest(".favorites-clear-all")) {
                e.preventDefault();
                this.clearAllFavorites();
            }

            // Remove individual favorite
            if (e.target.closest(".favorite-remove")) {
                e.preventDefault();
                const productId = e.target.closest(
                    ".favorite-remove"
                ).dataset.productId;
                this.removeFromFavorites(productId);
            }
        });

        // Handle modal outside click
        document.addEventListener("click", (e) => {
            const modal = document.getElementById(
                "favorites-modal"
            );
            if (modal && e.target === modal) {
                this.hideFavoritesModal();
            }
        });

        // Handle escape key
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                this.hideFavoritesModal();
            }
        });
    }

    getFavoritesFromStorage() {
        try {
            const stored =
                localStorage.getItem("favorites");
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error(
                "Error loading favorites:",
                error
            );
            return [];
        }
    }

    saveFavoritesToStorage() {
        try {
            localStorage.setItem(
                "favorites",
                JSON.stringify(this.favorites)
            );
        } catch (error) {
            console.error("Error saving favorites:", error);
        }
    }

    addToFavorites(productData) {
        // Check if already in favorites
        if (this.isInFavorites(productData.id)) {
            return false;
        }

        this.favorites.push({
            id: productData.id,
            title: productData.title,
            handle: productData.handle,
            image: productData.image,
            url: productData.url,
            sku: productData.sku || "",
            addedAt: new Date().toISOString(),
        });

        this.saveFavoritesToStorage();
        this.updateFavoriteCounter();
        this.updateFavoriteButtons();
        this.showNotification(
            this.getTranslation("notification_added", {
                product_title: productData.title,
            })
        );

        return true;
    }

    removeFromFavorites(productId) {
        const index = this.favorites.findIndex(
            (fav) => fav.id === productId
        );
        if (index > -1) {
            const product = this.favorites[index];
            this.favorites.splice(index, 1);
            this.saveFavoritesToStorage();
            this.updateFavoriteCounter();
            this.updateFavoriteButtons();
            this.updateFavoritesModal();
            this.showNotification(
                this.getTranslation(
                    "notification_removed",
                    { product_title: product.title }
                )
            );
            return true;
        }
        return false;
    }

    isInFavorites(productId) {
        return this.favorites.some(
            (fav) => fav.id === productId
        );
    }

    clearAllFavorites() {
        if (this.favorites.length === 0) return;

        this.favorites = [];
        this.saveFavoritesToStorage();
        this.updateFavoriteCounter();
        this.updateFavoriteButtons();
        this.updateFavoritesModal();
        this.showNotification(
            this.getTranslation("notification_cleared")
        );
    }

    handleFavoriteToggle(button) {
        const productData = {
            id: button.dataset.productId,
            title: button.dataset.productTitle,
            handle: button.dataset.productHandle,
            image: button.dataset.productImage,
            url: button.dataset.productUrl,
            sku: button.dataset.productSku || "",
        };

        if (this.isInFavorites(productData.id)) {
            this.removeFromFavorites(productData.id);
        } else {
            this.addToFavorites(productData);
        }
    }

    updateFavoriteCounter() {
        const counters = document.querySelectorAll(
            ".favorites-count"
        );
        counters.forEach((counter) => {
            const count = this.favorites.length;
            counter.textContent = count;
            counter.style.display =
                count > 0 ? "flex" : "none";
        });
    }

    updateFavoriteButtons() {
        // Update all favorite buttons on the page - both product card and product page buttons
        document
            .querySelectorAll(
                ".favorite-button, .product-page-favorite-button"
            )
            .forEach((button) => {
                const productId = button.dataset.productId;
                const isInFavorites =
                    this.isInFavorites(productId);

                button.classList.toggle(
                    "active",
                    isInFavorites
                );

                // Handle PNG icon switching
                const defaultIcon = button.querySelector(
                    ".favorite-icon-default"
                );
                const activeIcon = button.querySelector(
                    ".favorite-icon-active"
                );

                if (defaultIcon && activeIcon) {
                    if (isInFavorites) {
                        defaultIcon.style.display = "none";
                        activeIcon.style.display = "block";
                    } else {
                        defaultIcon.style.display = "block";
                        activeIcon.style.display = "none";
                    }
                }

                const title = isInFavorites
                    ? this.getTranslation(
                          "remove_from_favorites"
                      )
                    : this.getTranslation(
                          "add_to_favorites"
                      );
                button.title = title;
                button.setAttribute("aria-label", title);
            });
    }

    showFavoritesModal() {
        const modal = document.getElementById(
            "favorites-modal"
        );
        if (modal) {
            this.updateFavoritesModal();
            modal.style.display = "flex";
            document.body.style.overflow = "hidden";

            // Focus management
            setTimeout(() => {
                const firstFocusable = modal.querySelector(
                    ".favorites-modal-close"
                );
                if (firstFocusable) firstFocusable.focus();
            }, 100);
        }
    }

    hideFavoritesModal() {
        const modal = document.getElementById(
            "favorites-modal"
        );
        if (modal) {
            modal.style.display = "none";
            document.body.style.overflow = "";
        }
    }

    updateFavoritesModal() {
        const modal = document.getElementById(
            "favorites-modal"
        );
        if (!modal) return;

        const content = modal.querySelector(
            ".favorites-modal-content"
        );
        if (!content) return;

        if (this.favorites.length === 0) {
            content.innerHTML = `
                <div class="favorites-modal-header">
                    <h2>${this.getTranslation(
                        "my_favorites"
                    )}</h2>
                    <button class="favorites-modal-close" aria-label="${this.getTranslation(
                        "close"
                    )}">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M13 1L1 13M13 13L1 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="favorites-empty">
                    <p>${this.getTranslation(
                        "no_favorites"
                    )}</p>
                    <p>${this.getTranslation(
                        "start_favoriting"
                    )}</p>
                </div>
            `;
        } else {
            const favoritesHtml = this.favorites
                .map(
                    (fav) => `
                <div class="favorite-item">
                    <div class="favorite-item-image" data-product-url="${
                        fav.url
                    }">
                        <img src="${fav.image}" alt="${
                        fav.title
                    }" loading="lazy">
                    </div>
                    <div class="favorite-item-details" data-product-url="${
                        fav.url
                    }">
                        <h3><a href="${fav.url}">${
                        fav.title
                    }</a></h3>
                        ${
                            fav.sku
                                ? `<div class="favorite-item-sku">SKU: ${fav.sku}</div>`
                                : ""
                        }
                    </div>
                    <button class="favorite-remove" data-product-id="${
                        fav.id
                    }" aria-label="${this.getTranslation(
                        "remove_from_favorites"
                    )}">
                        <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
                            <path d="M12 3L1 3M5.00024 6V10M8.00024 6V10M11.0002 3V12.5C11.0002 12.6326 10.9476 12.7598 10.8538 12.8536C10.76 12.9473 10.6329 13 10.5002 13H2.50024C2.36764 13 2.24046 12.9473 2.14669 12.8536C2.05292 12.7598 2.00024 12.6326 2.00024 12.5V3M9.00025 3V2C9.00025 1.73478 8.89489 1.48043 8.70735 1.29289C8.51982 1.10536 8.26546 1 8.00024 1H5.00024C4.73503 1 4.48067 1.10536 4.29314 1.29289C4.1056 1.48043 4.00024 1.73478 4.00024 2V3" stroke="#E93636" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            `
                )
                .join("");

            content.innerHTML = `
                <div class="favorites-modal-header">
                    <h2>${this.getTranslation(
                        "my_favorites"
                    )} (${this.favorites.length})</h2>
                    <button class="favorites-modal-close" aria-label="${this.getTranslation(
                        "close"
                    )}">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M13 1L1 13M13 13L1 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="favorites-list">
                    ${favoritesHtml}
                </div>
                <div class="favorites-modal-footer">
                    <button class="favorites-clear-all">${this.getTranslation(
                        "clear_all"
                    )}</button>
                </div>
            `;
        }
    }

    createFavoritesModal() {
        const modalHtml = `
            <div id="favorites-modal" class="favorites-modal" style="display: none;">
                <div class="favorites-modal-overlay"></div>
                <div class="favorites-modal-dialog">
                    <div class="favorites-modal-content">
                        <!-- Content will be dynamically generated -->
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML(
            "beforeend",
            modalHtml
        );
    }

    showNotification(message) {
        // Try multiple notification methods
        if (window.showToast) {
            window.showToast(message, "success");
        } else if (
            window.NotificationToast &&
            window.NotificationToast.show
        ) {
            window.NotificationToast.show(
                message,
                "success"
            );
        } else {
            // Simple fallback notification
            const toast = document.createElement("div");
            toast.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #4CAF50;
                color: white;
                padding: 12px 20px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 500;
                z-index: 10000;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                max-width: 300px;
                font-family: inherit;
                opacity: 0;
                transform: translateX(100%);
                transition: all 0.3s ease;
            `;
            toast.textContent = message;
            document.body.appendChild(toast);

            // Animate in
            setTimeout(() => {
                toast.style.opacity = "1";
                toast.style.transform = "translateX(0)";
            }, 10);

            // Auto hide
            setTimeout(() => {
                toast.style.opacity = "0";
                toast.style.transform = "translateX(100%)";
                setTimeout(() => {
                    if (toast.parentNode) {
                        document.body.removeChild(toast);
                    }
                }, 300);
            }, 3000);
        }
    }

    getTranslation(key, variables = {}) {
        // Get translations from global theme object
        const translations =
            window.theme &&
            window.theme.translations &&
            window.theme.translations.favorites;

        let translation = translations[key];

        // Replace variables
        Object.keys(variables).forEach((variable) => {
            translation = translation.replace(
                `{{ ${variable} }}`,
                variables[variable]
            );
        });

        return translation;
    }
}

// Initialize favorites when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
    window.favorites = new Favorites();
});
